import { useState } from 'react';
import { HousePlus } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { getGoogleStaticMapForBuildHub } from '@modules/build-hub/utils/get-google-static-map';
import { useRootData } from '@modules/root';

export interface ProjectThumbnailProps {
    project: BuildHubOption;
    /** Rendered size in px; the map is requested to match. */
    size: 40 | 48;
    className?: string;
}

/**
 * A project's picture wherever it is listed. Falls back to a map of its
 * location, which reads as that project far better than a generic tile — but
 * only while the image actually loads: the map needs a key the environment may
 * not have, and a broken-image glyph is worse than the plain tile it replaced.
 */
export const ProjectThumbnail = ({
    project,
    size,
    className,
}: ProjectThumbnailProps) => {
    const [failed, setFailed] = useState(false);
    const { config } = useRootData();
    const place = project.LocationData?.fullAddress || project.Location;
    const mapKey = config.pixels.websiteGoogleMapKey;

    const source =
        project.Thumbnail ||
        (place && mapKey
            ? getGoogleStaticMapForBuildHub(place, mapKey, {
                  width: size * 2,
                  height: size * 2,
                  zoomLevel: 12,
                  showMarker: false,
              })
            : null);

    return (
        <span
            className={cn(
                'flex shrink-0 items-center justify-center overflow-hidden rounded-[0.375rem] bg-black/5 text-black/25',
                size === 40 ? 'size-10' : 'size-12',
                className,
            )}
        >
            {source && !failed ? (
                <img
                    src={source}
                    alt=""
                    loading="lazy"
                    onError={() => setFailed(true)}
                    className="size-full object-cover"
                />
            ) : (
                <HousePlus aria-hidden className="size-4" />
            )}
        </span>
    );
};
